import axios, { AxiosInstance, AxiosError } from 'axios';
import { getAuthHeaders, handleUnauthorized } from '@/stores/authStore';

// Production build: same-origin relative requests (nginx reverse-proxies API paths to the backend).
// Dev server: VITE_API_URL or the local backend default.
const API_BASE_URL = import.meta.env.PROD ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

// Create axios instance with defaults
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth headers
apiClient.interceptors.request.use(
  (config) => {
    const authHeaders = getAuthHeaders();
    if (authHeaders.Authorization) {
      config.headers.Authorization = authHeaders.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - logout user
      console.warn('Unauthorized - logging out');
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

// API modules
export const documentsApi = {
  list: () => apiClient.get('/msb/documents'),
  
  get: (id: string) => apiClient.get(`/msb/documents/${id}`),
  
  upload: (file: File, options?: { 
    documentType?: string; 
    ocrModel?: string; 
    selectedPages?: string; 
    circular?: string;
    enableExtractJson?: boolean;
    enableRagIngest?: boolean;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.documentType) formData.append('document_type', options.documentType);
    if (options?.ocrModel) formData.append('ocr_model', options.ocrModel);
    if (options?.selectedPages) formData.append('selected_pages', options.selectedPages);
    if (options?.circular) formData.append('circular', options.circular);
    // Pipeline options - default to true if not specified
    formData.append('enable_extract_json', String(options?.enableExtractJson ?? true));
    formData.append('enable_rag_ingest', String(options?.enableRagIngest ?? true));
    
    return apiClient.post('/msb/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Status management
  updateStatus: (id: string, status: string) =>
    apiClient.patch(`/msb/documents/${id}/status`, { status }),
  
  approve: (id: string) =>
    apiClient.post(`/msb/documents/${id}/approve`),
    
  reject: (id: string, reason?: string) =>
    apiClient.post(`/msb/documents/${id}/reject`, { reason }),
  
  getStatus: (id: string) =>
    apiClient.get(`/msb/documents/${id}/status`),
  
  getForReview: (id: string) =>
    apiClient.get(`/msb/documents/${id}/for-review`),
  
  saveAudit: (id: string, data: any) =>
    apiClient.post(`/msb/documents/${id}/auditlog`, data),
    
  delete: (id: string) =>
    apiClient.delete(`/msb/documents/${id}`),

  // Re-run validation engine
  runValidation: (id: string) =>
    apiClient.post(`/msb/documents/${id}/validate`),

  // Update document metadata (company_name, tax_id, report_date, etc.)
  updateMetadata: (id: string, metadata: {
    company_name?: string;
    tax_id?: string;
    report_date?: string;
    currency_unit?: string;
    report_name?: string;
    document_name?: string;
  }) =>
    apiClient.post(`/msb/documents/${id}/metadata`, metadata),
};

export const authApi = {
  login: (username: string, password: string) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return apiClient.post('/auth/token', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  register: (username: string, password: string) =>
    apiClient.post('/auth/', { username, password }),
    
  me: () => apiClient.get('/auth/me'),
};

export const financialsApi = {
  getCompanyFinancials: (taxCode: string) =>
    apiClient.get(`/msb/company/${taxCode}/financials`),
  
  runValidation: (id: string) =>
    apiClient.post(`/msb/documents/${id}/validate`),
};

export const configApi = {
  getCircularMetadata: () =>
    apiClient.get('/msb/circular-metadata'),
  
  getDictionary: (circular: string) =>
    apiClient.get(`/msb/dictionary/${circular}`),
};

// Circular Config API - Thông tư management
export const circularConfigApi = {
  // List all circulars
  list: () =>
    apiClient.get('/msb/circular-config/'),
  
  // Get circular details
  get: (id: number) =>
    apiClient.get(`/msb/circular-config/${id}`),
  
  // Create new circular
  create: (data: {
    circular_code: string;
    circular_name: string;
    description?: string;
    regulation_year?: number;
  }) =>
    apiClient.post('/msb/circular-config/', data),
  
  // Update circular metadata
  update: (id: number, data: {
    circular_name?: string;
    description?: string;
    regulation_year?: number;
    is_active?: boolean;
  }) =>
    apiClient.put(`/msb/circular-config/${id}`, data),
  
  // Soft delete circular
  delete: (id: number) =>
    apiClient.delete(`/msb/circular-config/${id}`),
  
  // Get statement JSON
  getStatementJson: (id: number, statementType: 'balance_sheet' | 'income_statement' | 'cash_flow') =>
    apiClient.get(`/msb/circular-config/${id}/json/${statementType}`),
  
  // Update statement JSON
  updateStatementJson: (id: number, statementType: 'balance_sheet' | 'income_statement' | 'cash_flow', items: any[]) =>
    apiClient.put(`/msb/circular-config/${id}/json/${statementType}`, items),
};

// AI Models config - stored in localStorage, no backend yet
export interface AIModelConfig {
  id: string;
  modelType: 'gemini' | 'mistral' | 'ollama' | 'openai';
  modelName: string;
  apiKeyEnvVar?: string;
  baseUrl?: string;
  documentType?: 'BCTC' | 'THUYET_MINH' | 'RAG';
  description: string;
  isActive: boolean;
  parameters?: {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
  };
}

// Prompt Templates config
export interface PromptTemplate {
  id: string;
  name: string;
  category: 'ocr' | 'extract' | 'rag' | 'validation';
  prompt: string;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// RAG API - AI Agent ingestion and chat
export const ragApi = {
  // Start document ingestion into RAG system
  ingest: (documentId: string, useMistral: boolean = true) =>
    apiClient.post('/rag/ingest', { document_id: documentId, use_mistral: useMistral }),
  
  // Get ingestion status for a document
  getIngestStatus: (documentId: string) =>
    apiClient.get(`/rag/ingest/${documentId}/status`),
  
  // Chat with AI about a document
  chat: (query: string, documentId?: string, sessionId?: string) =>
    apiClient.post('/rag/chat', { query, document_id: documentId, session_id: sessionId }),
  
  // Get knowledge graph data for visualization
  getGraphData: (documentId: string) =>
    apiClient.get(`/rag/graph/${documentId}`),
};

// Audit Logs API
export const auditApi = {
  /** Paginated global audit logs (newest first) */
  list: (params?: {
    limit?: number;
    offset?: number;
    action_type?: string;
    action_status?: string;
    performed_by?: string;
    document_id?: string;
  }) =>
    apiClient.get('/audit/logs', { params }),

  /** Comprehensive audit for one document */
  getDocumentAudit: (documentId: string) =>
    apiClient.get(`/audit/documents/${documentId}/comprehensive`),

  /** Global statistics */
  getStatistics: (params?: { start_date?: string; end_date?: string }) =>
    apiClient.get('/audit/statistics', { params }),
};

export default apiClient;
